//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { PurchaseOptionsTableRowContent } from '@campaigns/components/shared/purchase-options/purchase-options-table-row/PurchaseOptionsTableRowContent';
import { WIDTH_CHECKBOX } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { getExpiryBadgeType } from '@campaigns/utils/sampling/samplingCampaignsPurchaseOptionsUtils';
import { TableDataCell } from '@components/organisms/table/TableDataCell';
import { TableRow } from '@components/organisms/table/TableRow';
import { Checkbox } from '@components/primitives/checkbox/Checkbox';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';

interface PurchaseOptionsTableRowEditableProps {
  purchaseOption: PurchaseOption;
}

export const PurchaseOptionsTableRowEditable = ({
  purchaseOption,
}: PurchaseOptionsTableRowEditableProps) => {
  const timeZone = useSelector(getUserTimeZone);
  const { updateFormValues, watchFormValues } = useSamplingCampaign();
  const formValues = watchFormValues();

  const isExpired = moment(purchaseOption.expiryDate).isBefore(moment());
  const expiryBadgeInfo = getExpiryBadgeType(
    purchaseOption.expiryDate,
    timeZone,
  );

  const isChecked = formValues.purchaseOption?.some(
    (option) => option.opportunityId === purchaseOption.opportunityId,
  );

  const handleCheckboxChange = () => {
    const updatedSelectedOptions = isChecked
      ? formValues.purchaseOption?.filter(
          (option: PurchaseOption) =>
            option.opportunityId !== purchaseOption.opportunityId,
        )
      : [...(formValues.purchaseOption ?? []), purchaseOption];

    updateFormValues('purchaseOption', updatedSelectedOptions);
  };

  return (
    <TableRow>
      <CheckboxCell scope='row'>
        <Checkbox checked={isChecked} onCheckedChange={handleCheckboxChange}>
          <Checkbox.Indicator>
            {isChecked ? (
              <Checkbox.Icon name='checkmark' />
            ) : (
              <Checkbox.Icon name='collapse' />
            )}
          </Checkbox.Indicator>
        </Checkbox>
      </CheckboxCell>
      <PurchaseOptionsTableRowContent
        expiryBadgeInfo={expiryBadgeInfo}
        isExpired={isExpired}
        purchaseOption={purchaseOption}
      />
    </TableRow>
  );
};

const CheckboxCell = styled(TableDataCell)`
  ${WIDTH_CHECKBOX}
`;
